import React from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '../Common/Modal/Modal';
import EllipsesControl from '../Common/EllipsesControl/EllipsesControl';
import {labels} from '../../util/uiconstants';
import styled from 'styled-components';
import Utility from '../../util/utils';
import MotifButton from '@ey/motif-react-wrapper/Button';

/**
 * EyServiceGatewayModal Component
 *
 * A modal component that provides users with options to navigate between EY Service Gateway and EY Canvas.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.show - Controls the visibility of the modal
 * @param {Function} props.setShow - Function to control the modal visibility state
 * @param {string} props.incountryuri - The base URI for the in-country EY Canvas instance
 * @param {string} props.engagementId - The unique identifier for the engagement
 * @param {string} props.engagementDescription - Description of the engagement
 * @param {boolean} [props.redirectToManageTeam=false] - Flag to indicate if redirection is for managing team
 * @returns {JSX.Element} A modal with navigation options for EY Service Gateway and EY Canvas
 **/
const EyServiceGatewayModal = ({
	show,
	setShow,
	incountryuri,
	engagementId,
	engagementDescription,
	redirectToManageTeam = false
}) => {
	const featureConfigurations = useSelector(
		(store) => store.featureConfiguration
	);

	const routeToEYService = () => {
		if (!featureConfigurations?.servicegatewaybaseuri) {
			// Service Gateway base URI is not configured; do not attempt navigation.
			return;
		}

		const queryParams = new URLSearchParams({engagementId}).toString();

		window.location.href =
			featureConfigurations?.servicegatewaybaseuri + '?' + queryParams;
	};
	const routeToEYCanvas = () => {
		if (!incountryuri) {
			// In-country URI is not provided; do not attempt navigation.
			return;
		}

		const queryParams = new URLSearchParams({engagementId}).toString();

		if (redirectToManageTeam) {
			const manageTeamUrl = incountryuri + '/manageteam?' + queryParams;
			window.location.href = manageTeamUrl;
		} else {
			const engagementurl = incountryuri + '/dashboard?' + queryParams;
			window.location.href = engagementurl;
		}
	};

	const handleClose = () => {
		setShow(false);
	};

	const handleEnterEngagement = () => {
		routeToEYCanvas();
		setShow(false);
	};

	const handleContinue = () => {
		routeToEYService();
		setShow(false);
	};

	return (
		<Modal
			modalsize="x-small"
			modalContainerClass="blocker-informational-modal"
			role="dialog"
			headerText={
				<EllipsesControl
					content={labels.eyServiceGatewayModalTitle}
					tooltip={labels.eyServiceGatewayModalTitle}
					noOfLines={1}
				/>
			}
			show={show}
			onClose={handleClose}
			showFooter
			customFooter={() => (
				<StyledFooter className="custom-footer">
					<MotifButton
						variant="ghost"
						size="medium"
						className="entereng-btn"
						onClick={handleEnterEngagement}
					>
						<EllipsesControl
							content={labels.enterEngagementButtonLabel}
							tooltip={labels.enterEngagementButtonLabel}
							noOfLines={1}
						/>
					</MotifButton>
					<MotifButton
						variant="primary"
						size="medium"
						className="continue-btn"
						onClick={handleContinue}
					>
						<EllipsesControl
							content={labels.continue}
							tooltip={labels.continue}
							noOfLines={1}
						/>
					</MotifButton>
				</StyledFooter>
			)}
		>
			<StyledEyServiceGatewayModalBody className="motif-body1-default-light">
				<p className="msg-description">
					{Utility.createRichTextElement(labels.eyServiceGatewayModalBodyText)}
				</p>
				<EllipsesControl
					ellipsisClass="eng-description"
					content={engagementDescription}
					tooltip={engagementDescription}
					noOfLines={1}
				/>
			</StyledEyServiceGatewayModalBody>
		</Modal>
	);
};

export default EyServiceGatewayModal;

EyServiceGatewayModal.propTypes = {
	show: PropTypes.bool.isRequired,
	setShow: PropTypes.func.isRequired,
	incountryuri: PropTypes.string,
	engagementId: PropTypes.string.isRequired,
	engagementDescription: PropTypes.string.isRequired,
	redirectToManageTeam: PropTypes.bool
};

const StyledEyServiceGatewayModalBody = styled.section`
	p {
		margin-bottom: var(--px-16);
	}

	.msg-description {
		padding-bottom: var(--px-16);
		border-bottom: var(--px-1) solid var(--neutrals-300);
	}

	.eng-description {
		font-weight: bold;
		font-size: var(--px-14);
	}
`;

const StyledFooter = styled.section`
	display: flex;
	justify-content: flex-end;
	border-top: var(--px-1) solid var(--neutrals-100);
	padding: var(--spacing-x-large);
	gap: var(--spacing-medium);
`;
